<template>
    <div class="oauth-authorize-container">
        <div class="card">
            <div class="card-header">
                <h1>Authorize Application</h1>
                <div class="app-info">
                    <p>
                        An application would like to connect to your Mystekz
                        account
                    </p>
                </div>
            </div>

            <div class="card-body">
                <div class="p-field">
                    <label for="organization-select" class="form-label"
                        >Organization</label
                    >
                    <Select
                        id="organization-select"
                        v-model="selectedOrganization"
                        :options="organizations"
                        optionLabel="name"
                        placeholder="Select an organization"
                        class="w-full"
                        @change="onOrganizationChange"
                    />
                    <small class="form-help-text"
                        >Select the organization you want to grant access
                        to.</small
                    >
                </div>

                <div class="p-field">
                    <label for="stek-select" class="form-label">Stek</label>
                    <Select
                        id="stek-select"
                        v-model="selectedStek"
                        :options="steks"
                        optionLabel="name"
                        placeholder="Select a stek"
                        class="w-full"
                        :disabled="!selectedOrganization || loadingSteks"
                    />
                    <small v-if="loadingSteks" class="form-help-text">
                        <i class="pi pi-spin pi-spinner mr-2"></i>Loading
                        steks...
                    </small>
                    <small v-else class="form-help-text"
                        >Select the stek you want to grant access to.</small
                    >
                </div>

                <div class="scope-info">
                    <h3>This application will be able to:</h3>
                    <ul class="scope-list">
                        <li v-if="scope.includes('mcp')">
                            <i class="pi pi-check-circle"></i>
                            <span
                                >Access the MyStekz Collaboration Platform
                                (MCP)</span
                            >
                        </li>
                    </ul>
                </div>
            </div>

            <div class="card-footer">
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    class="p-button-text"
                    @click="cancelAuthorization"
                />

                <!-- Hidden form for OAuth authorization -->
                <form
                    ref="authForm"
                    method="POST"
                    :action="`${config.bekendURI}/integrations/oauth2/server/authorize`"
                    style="display: inline"
                >
                    <!-- OAuth parameters -->
                    <input type="hidden" name="client_id" :value="clientId" />
                    <input
                        type="hidden"
                        name="redirect_uri"
                        :value="redirectUri"
                    />
                    <input type="hidden" name="scope" :value="scope" />
                    <input
                        type="hidden"
                        name="code_challenge"
                        :value="codeChallenge"
                    />
                    <input
                        type="hidden"
                        name="code_challenge_method"
                        :value="codeChallengeMethod"
                    />
                    <input
                        type="hidden"
                        name="state"
                        :value="route.query.state || ''"
                    />

                    <!-- User selections -->
                    <input
                        type="hidden"
                        name="organization_id"
                        :value="selectedOrganization?.identity || ''"
                    />
                    <input
                        type="hidden"
                        name="stek_id"
                        :value="selectedStek?.identity || ''"
                    />

                    <!-- Auth headers (will be added by JavaScript before submission) -->
                    <input
                        type="hidden"
                        name="authorization_token"
                        :value="authToken"
                    />
                </form>

                <Button
                    label="Authorize"
                    icon="pi pi-check"
                    @click="authorizeClient"
                    :disabled="!isFormValid || isLoading"
                    :loading="isLoading"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import config from "@/config";
import { createClient } from "@/lib/openapi/client";
import * as services from "@/lib/openapi/gen/sdk.gen";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { useOrganizationsManager } from "@/stores/organizations-store";
import { Organization } from "@/stores/organizations-store";
import { Stek } from "@/stores/steks-store";

const route = useRoute();
const router = useRouter();
const { organizations } = useOrganizationsManager();

// OAuth parameters from URL
const clientId = ref<string>("");
const responseType = ref<string>("");
const codeChallenge = ref<string>("");
const codeChallengeMethod = ref<string>("");
const redirectUri = ref<string>("");
const scope = ref<string>("");

// Form state
const selectedOrganization = ref<Organization>();
const selectedStek = ref<Stek>();
const steks = ref<Stek[]>([]);
const loadingSteks = ref(false);
const isLoading = ref(false);
const authToken = ref<string>("");
const authForm = ref<HTMLFormElement>();

// Validation
const isFormValid = computed(() => {
    return Boolean(selectedOrganization.value && selectedStek.value);
});

onMounted(() => {
    // Extract URL query parameters
    clientId.value = (route.query.client_id as string) || "";
    responseType.value = (route.query.response_type as string) || "";
    codeChallenge.value = (route.query.code_challenge as string) || "";
    codeChallengeMethod.value =
        (route.query.code_challenge_method as string) || "";
    redirectUri.value = (route.query.redirect_uri as string) || "";
    scope.value = (route.query.scope as string) || "";
});

/**
 * Fetch steks for a specific organization
 */
const fetchSteksByOrganization = async (
    organizationId: string
): Promise<Stek[]> => {
    if (!organizationId) {
        console.warn("Cannot fetch steks: No organization ID provided");
        return [];
    }

    try {
        const directClient = createClient();

        // Add a request interceptor to manually add auth token
        directClient.interceptors.request.use(async (request) => {
            const { getToken } = useAuthenticationStore();
            const token = await getToken();

            if (token) {
                request.headers.set("Authorization", `Bearer ${token}`);
            }

            return request;
        });

        const controller = new AbortController();

        const { response, data } =
            await services.startStekStekMgtGetSteksByOrganizationStartPost({
                headers: {
                    "Organization": organizationId,
                },
                body: {},
                signal: controller.signal,
                client: directClient,
            });

        if (response.ok) {
            return data || [];
        } else {
            if (response.status === 0) {
                // Request was aborted
                return [];
            }

            throw new Error(`Request failed with status ${response.status}`);
        }
    } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
            // Request was aborted, ignore error
            return [];
        }

        console.error("Error fetching steks for organization:", err);
        return [];
    }
};

// Load steks when organization changes
const onOrganizationChange = async () => {
    if (!selectedOrganization.value) {
        steks.value = [];
        selectedStek.value = undefined;
        return;
    }

    loadingSteks.value = true;
    try {
        steks.value = await fetchSteksByOrganization(
            selectedOrganization.value.identity
        );
    } catch (error) {
        console.error("Error loading steks:", error);
        steks.value = [];
    } finally {
        loadingSteks.value = false;
    }
};

const authorizeClient = async () => {
    if (!isFormValid.value || !authForm.value) return;

    isLoading.value = true;

    try {
        // Get auth token and set it in the form
        const { getToken } = useAuthenticationStore();
        const token = await getToken();
        console.log(
            "OAuth Authorization - Retrieved token:",
            token ? `${token.substring(0, 20)}...` : "EMPTY/NULL"
        );

        if (!token) {
            console.error("No authentication token available");
            alert(
                "Authentication token not available. Please refresh and try again."
            );
            isLoading.value = false;
            return;
        }

        // Set the token in the hidden input field directly
        const authTokenInput = authForm.value.querySelector(
            'input[name="authorization_token"]'
        ) as HTMLInputElement;
        if (authTokenInput) {
            authTokenInput.value = token;
            console.log("OAuth Authorization - Token set in form field");
        } else {
            console.error(
                "OAuth Authorization - Could not find authorization_token input field"
            );
        }

        // Add organization and stek headers as hidden inputs
        const orgInput = document.createElement("input");
        orgInput.type = "hidden";
        orgInput.name = "organization_header";
        orgInput.value = selectedOrganization.value!.identity;
        authForm.value.appendChild(orgInput);

        const stekInput = document.createElement("input");
        stekInput.type = "hidden";
        stekInput.name = "stek_header";
        stekInput.value = selectedStek.value!.identity;
        authForm.value.appendChild(stekInput);

        // Submit the form - this will cause a natural browser navigation
        // which allows the backend to redirect to the OAuth client without CORS issues
        authForm.value.submit();
    } catch (error) {
        console.error("Error authorizing client:", error);
        isLoading.value = false;
    }
};

const cancelAuthorization = () => {
    // Redirect back to home or previous page
    router.push("/");
};
</script>

<style scoped lang="scss">
.oauth-authorize-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 2rem;
    background-color: #f8f9fa;
}

.card {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 600px;
    overflow: hidden;
}

.card-header {
    padding: 1.5rem;
    border-bottom: 1px solid #e9ecef;
    background-color: #f8f9fa;

    h1 {
        margin: 0 0 0.5rem;
        font-size: 1.75rem;
    }

    .app-info {
        color: #6c757d;
    }
}

.card-body {
    padding: 1.5rem;
}

.card-footer {
    padding: 1.5rem;
    border-top: 1px solid #e9ecef;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

.p-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 1.5rem;
}

.form-label {
    margin-bottom: 0.75rem;
    font-weight: 600;
    display: block;
}

.form-help-text {
    display: block;
    color: #6c757d;
    margin-top: 0.75rem;
    font-size: 0.875rem;
}

.scope-info {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;

    h3 {
        font-size: 1.1rem;
        margin-bottom: 1rem;
    }
}

.scope-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
        display: flex;
        align-items: center;
        margin-bottom: 0.5rem;

        i {
            color: #28a745;
            margin-right: 0.75rem;
        }
    }
}

.w-full {
    width: 100%;
}

.mr-2 {
    margin-right: 0.5rem;
}
</style>
